import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginUser, error: authError, currentUser } = useContext(AuthContext);

  const isExpired = searchParams.get('expired') === 'true';
  const requestedReturn = searchParams.get('returnTo') || '/dashboard';
  const returnTo = requestedReturn.startsWith('/') && !requestedReturn.startsWith('//')
    && !/[\\\r\n]/.test(requestedReturn) ? requestedReturn : '/dashboard';
  
  // Redirect if already logged in
  useEffect(() => {
    if (currentUser) {
      navigate(returnTo);
    }
  }, [currentUser, navigate, returnTo]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const result = await loginUser({ email, password });
      
      if (result.success) {
        setMessage('Login successful');
        // A delayed reload would interrupt the bridge's popup handoff.
        if (returnTo.split('?')[0] !== '/auth/redirect-to-cip') {
          setTimeout(() => { window.location.href = returnTo; }, 300);
        }
      } else {
        if (result.status === 429) {
          const waitText = result.retryAfterSeconds > 0
            ? ` Please wait about ${Math.ceil(result.retryAfterSeconds / 60)} minute(s) before retrying.`
            : ' Please wait a bit before retrying.';
          setMessage(`${result.message || 'Too many requests.'}${waitText}`);
        } else {
          setMessage(authError || result.message || 'Login failed');
        }
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Login error details:", error);
      setMessage('Server error: ' + (error.message || 'Unknown error'));
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container rebranded">
      <div className="login-card">
        <h2>Welcome to RSMIN Portal</h2>
        <p className="tagline">Empowering Education, Empowering You</p>
        
        {isExpired && !message && (
          <p className="error-message">Your session has expired. Please log in again.</p>
        )}
        {message && <p className={message === 'Login successful' ? 'success-message' : 'error-message'}>{message}</p>}
        
        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          
          <button 
            type="submit" 
            className="login-button" 
            disabled={isLoading}
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        
        <p className="register-link">
          Don't have an account? <Link to="/register">Register here</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;